import { flattenSections } from './numbering.js';
import type { DocumentContent, RichNode } from './types.js';

/** Plain text of a rich body — used for search indexing and AI prompts. */
export function richToText(nodes: RichNode[] | undefined): string {
  const parts: string[] = [];
  const walk = (node: RichNode) => {
    if (node.type === 'text' && node.text) parts.push(node.text);
    else if (node.type === 'crossReference') parts.push(String((node.attrs as { label?: string } | undefined)?.label ?? ''));
    else if (node.type === 'hardBreak') parts.push('\n');
    for (const child of node.content ?? []) walk(child);
    if (['paragraph', 'heading', 'listItem', 'taskItem', 'codeBlock', 'tableRow'].includes(node.type)) parts.push('\n');
  };
  for (const node of nodes ?? []) walk(node);
  return parts.join('').replace(/\n{3,}/g, '\n\n').trim();
}

export function contentToText(content: DocumentContent): string {
  return flattenSections(content)
    .map((entry) => {
      const heading = entry.number ? `${entry.number} ${entry.section.title}` : entry.section.title;
      const body = richToText(entry.section.body.content);
      return body ? `${heading}\n${body}` : heading;
    })
    .join('\n\n');
}

/**
 * Counts words in section titles and bodies. Generated clause numbers are
 * excluded so the count does not drift when sections are reordered.
 */
export function wordCount(content: DocumentContent): number {
  let total = 0;
  const count = (text: string) => {
    const words = text.match(/[\p{L}\p{N}][\p{L}\p{N}'’-]*/gu);
    return words ? words.length : 0;
  };
  for (const entry of flattenSections(content)) {
    total += count(entry.section.title);
    total += count(richToText(entry.section.body.content));
  }
  return total;
}
